import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { getMyReports, deleteReport, getCredits, getPricing, getWatchlist } from "@/lib/api";
import Header from "@/components/Header";
import { ArrowRight, Trash2, Zap, Calendar, CreditCard, Eye } from "lucide-react";

export default function MyReports() {
  const { user, loading } = useAuth();
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState(null);
  const [credits, setCredits] = useState(null);
  const [packages, setPackages] = useState([]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/my-reports";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { setBusy(false); return; }
    (async () => {
      try {
        const [list, c, pk] = await Promise.all([
          getMyReports(),
          getCredits().catch(() => null),
          getPricing().catch(() => []),
        ]);
        setReports(list);
        setCredits(c);
        setPackages(pk || []);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load");
      } finally {
        setBusy(false);
      }
    })();
  }, [user, loading]);

  const onDelete = async (id) => {
    await deleteReport(id);
    setReports((rs) => rs.filter((r) => r.id !== id));
  };

  if (loading || busy) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 font-mono-plex text-sm" data-testid="my-reports-loading">
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen grain" data-testid="my-reports-anon">
        <Header />
        <main className="max-w-3xl mx-auto px-6 md:px-10 py-20">
          <div className="border-2 border-[#111] bg-white p-10 shadow-brutal-lg">
            <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">▸ Reserved Section</div>
            <h1 className="mt-2 font-serif-display text-4xl md:text-5xl font-black tracking-tight leading-none">
              Sign in to save Blueprints
            </h1>
            <p className="mt-4 text-[#333]">
              Anonymous scans are great. But if you want to keep a history, compare URLs, and revisit your top plays — sign in with Google.
            </p>
            <button
              onClick={handleLogin}
              className="mt-6 inline-flex items-center gap-3 bg-[#111] text-[#F4F4F0] px-6 py-4 font-mono-plex text-sm uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors shadow-brutal"
              data-testid="my-reports-login"
            >
              Continue with Google <ArrowRight size={16} />
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen grain" data-testid="my-reports-page">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
            ▸ Archive · {user.name}
          </span>
          <span className="flex-1 h-px bg-[#111]" />
        </div>
        <h1 className="font-serif-display text-5xl md:text-6xl font-black tracking-tighter leading-[0.9]">
          My Blueprints
        </h1>

        <PlanSummary credits={credits} packages={packages} />

        {err && <p className="mt-6 text-[#FF331F] font-mono-plex text-sm">{err}</p>}

        {reports.length === 0 ? (
          <div className="mt-10 border-2 border-dashed border-[#888] p-10 text-center" data-testid="my-reports-empty">
            <p className="font-mono-plex text-sm uppercase tracking-widest text-[#555]">No Blueprints yet.</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-5 py-3 font-mono-plex text-xs uppercase tracking-widest hover:bg-[#FF331F]"
              data-testid="empty-scan-link"
            >
              Run your first analysis <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <div className="mt-10 border-2 border-[#111] bg-white" data-testid="my-reports-list">
            {reports.map((r, i) => (
              <div
                key={r.id}
                className={`grid grid-cols-1 md:grid-cols-12 gap-4 p-5 items-center ${i < reports.length - 1 ? "border-b-2 border-[#111]" : ""}`}
                data-testid={`report-row-${r.id}`}
              >
                <div className="md:col-span-7">
                  <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                  <Link to={`/report/${r.id}`} className="font-serif-display text-2xl font-bold leading-tight hover:text-[#FF331F]">
                    {r.title}
                  </Link>
                  <div className="font-mono-plex text-xs text-[#555] truncate">{r.url}</div>
                </div>
                <div className="md:col-span-3 flex flex-wrap gap-1.5">
                  {r.recommendations?.slice(0, 3).map((rec, j) => (
                    <span key={j} className="font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] px-2 py-0.5 bg-[#F4F4F0]">
                      {rec.monetization_type} · {rec.fit_score}
                    </span>
                  ))}
                </div>
                <div className="md:col-span-2 flex gap-2 md:justify-end">
                  <Link
                    to={`/report/${r.id}`}
                    className="font-mono-plex text-xs uppercase tracking-widest border border-[#111] px-3 py-2 hover:bg-[#111] hover:text-[#F4F4F0]"
                    data-testid={`open-report-${r.id}`}
                  >
                    Open
                  </Link>
                  <button
                    onClick={() => onDelete(r.id)}
                    className="font-mono-plex text-xs uppercase tracking-widest border border-[#111] px-3 py-2 hover:bg-[#FF331F] hover:text-white hover:border-[#FF331F]"
                    data-testid={`delete-report-${r.id}`}
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

// ============ PLAN SUMMARY ============
// Shows the user what they paid for: plan name, renewal countdown, lookup breakdown.
// NOTE: billing management (cancel/card) intentionally does NOT live here — it's on
// /account behind the avatar, Grammarly-style, away from the daily workspace.
function PlanSummary({ credits, packages }) {
  const [watchUsage, setWatchUsage] = useState(null);
  const hasSubEarly = credits?.subscription_status === "active";
  useEffect(() => {
    if (!hasSubEarly) return;
    getWatchlist().then(setWatchUsage).catch(() => {});
  }, [hasSubEarly]);

  if (!credits) return null;

  const hasSub = credits.subscription_status === "active";
  const hasPerm = (credits.credits ?? 0) > 0;

  // Not paying yet — nudge to pricing (this is different from clicking the badge because they land here from clicking "N LOOKUPS" which means they have some)
  if (!hasSub && !hasPerm) {
    return (
      <div
        className="mt-8 border-2 border-dashed border-[#888] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4"
        data-testid="plan-summary-empty"
      >
        <div className="flex-1">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555]">▸ No active plan</div>
          <h2 className="mt-1 font-serif-display text-2xl font-bold">You&apos;re out of lookups.</h2>
          <p className="mt-1 text-sm text-[#333]">
            Grab a Single ($29) or start a monthly plan — Starter kicks in from $49/mo.
          </p>
        </div>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-5 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors shadow-brutal shrink-0"
          data-testid="plan-buy-cta"
        >
          See plans <ArrowRight size={14} />
        </Link>
      </div>
    );
  }

  // Resolve current plan name (from packages list matched by subscription_plan id)
  const currentPkg = packages.find((p) => p.id === credits.subscription_plan);
  const planName = hasSub ? (currentPkg?.name || "Monthly plan") : "Single Blueprint";
  const planPrice = hasSub && currentPkg?.price != null
    ? `$${currentPkg.price}/mo`
    : hasSub ? null : "$29 · one-time";

  // Renewal countdown
  let daysLeft = null;
  let renewsOn = null;
  if (hasSub && credits.subscription_expires_at) {
    const exp = new Date(credits.subscription_expires_at);
    daysLeft = Math.max(0, Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24)));
    renewsOn = exp.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  }

  return (
    <div
      className="mt-8 border-2 border-[#111] bg-white shadow-brutal p-6 md:p-8"
      data-testid="plan-summary"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-4">
        {/* Column 1 — Plan */}
        <div data-testid="plan-summary-plan">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
            <CreditCard size={11} /> ▸ Your plan
          </div>
          <div className="mt-2 font-serif-display text-3xl font-black leading-none">
            {planName}
          </div>
          {planPrice && (
            <div className="mt-1 font-mono-plex text-xs text-[#555]">{planPrice}</div>
          )}
          {hasSub && (
            <span className="mt-3 inline-block font-mono-plex text-[10px] uppercase tracking-widest bg-[#FF331F] text-white px-2 py-1">
              Active
            </span>
          )}
        </div>

        {/* Column 2 — Lookups */}
        <div data-testid="plan-summary-lookups">
          <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
            <Zap size={11} /> ▸ Lookups
          </div>
          <div className="mt-2 font-serif-display text-3xl font-black leading-none">
            {credits.total}
          </div>
          {hasSub ? (
            <div className="mt-1 font-mono-plex text-xs text-[#555]">
              {credits.monthly_credits} monthly · {credits.credits} bonus pack
            </div>
          ) : (
            <div className="mt-1 font-mono-plex text-xs text-[#555]">Never expire</div>
          )}
          {hasSub && watchUsage && (
            <Link
              to="/watchlist"
              className="mt-3 inline-flex items-center gap-1.5 font-mono-plex text-[10px] uppercase tracking-widest border border-[#111] px-2 py-1 hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
              data-testid="plan-monitored-usage"
            >
              <Eye size={10} /> {watchUsage.used} of {watchUsage.limit} monitored websites
            </Link>
          )}
        </div>

        {/* Column 3 — Renewal / CTA */}
        <div data-testid="plan-summary-renewal">
          {hasSub && daysLeft !== null ? (
            <>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
                <Calendar size={11} /> ▸ {credits.auto_renew ? "Renews in" : "Resets in"}
              </div>
              <div className="mt-2 font-serif-display text-3xl font-black leading-none">
                {daysLeft} <span className="text-lg font-normal">day{daysLeft === 1 ? "" : "s"}</span>
              </div>
              <div className="mt-1 font-mono-plex text-xs text-[#555]">
                on {renewsOn}{credits.auto_renew ? " · auto-renews" : ""}
              </div>
            </>
          ) : (
            <>
              <div className="font-mono-plex text-[10px] uppercase tracking-widest text-[#555] flex items-center gap-1.5">
                ▸ Need more?
              </div>
              <div className="mt-2 font-mono-plex text-xs text-[#333] leading-relaxed">
                Upgrade to a monthly plan for {" "}unlimited rescans and diff views.
              </div>
            </>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 border-2 border-[#111] px-4 py-2 font-mono-plex text-[10px] uppercase tracking-widest hover:bg-[#111] hover:text-[#F4F4F0]"
              data-testid="plan-manage-link"
            >
              {hasSub ? "Change plan" : "Upgrade"} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
